import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Icon from '../../../components/AppIcon';
import { hrAPI } from '../../../api/hr';
import { ATTENDANCE_STATUSES, DEFAULT_PAGINATION } from '../constants';
import AttendanceFormModal from './AttendanceFormModal';

const PAGE_SIZE = 10;

const statusBadge = {
  Present: 'bg-green-50 text-green-700',
  Absent: 'bg-red-50 text-red-700',
  Leave: 'bg-amber-50 text-amber-700',
  'Half Day': 'bg-blue-50 text-blue-700',
  Holiday: 'bg-indigo-50 text-indigo-700',
  Weekend: 'bg-gray-100 text-gray-700'
};

const formatDate = (value) => {
  if (!value) return '-';
  return new Date(value).toLocaleDateString();
};

const formatTime = (value) => {
  if (!value) return '-';
  return new Date(value).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const AttendanceTab = ({
  employeeOptions = [],
  employeesLoading = false,
  showSuccess,
  showError,
  onDataChanged
}) => {
  const [filters, setFilters] = useState({
    user: '',
    status: '',
    startDate: '',
    endDate: '',
    isApproved: '',
    page: 1
  });
  const [records, setRecords] = useState([]);
  const [pagination, setPagination] = useState({ ...DEFAULT_PAGINATION, limit: PAGE_SIZE });
  const [loading, setLoading] = useState(true);
  const [modalState, setModalState] = useState({ open: false, mode: 'create', record: null });
  const [deletingId, setDeletingId] = useState(null);

  const fetchAttendance = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: filters.page,
        limit: PAGE_SIZE,
        user: filters.user,
        status: filters.status || undefined,
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined
      };
      if (filters.isApproved !== '') {
        params.isApproved = filters.isApproved;
      }
      const response = await hrAPI.listAttendance(params);
      setRecords(response?.items || []);
      setPagination({
        ...DEFAULT_PAGINATION,
        limit: PAGE_SIZE,
        ...(response?.pagination || {})
      });
    } catch (error) {
      showError?.(error.message || 'Failed to load attendance records');
    } finally {
      setLoading(false);
    }
  }, [filters, showError]);

  useEffect(() => {
    fetchAttendance();
  }, [fetchAttendance]);

  const totalPages = useMemo(() => Math.max(pagination.pages || 1, 1), [pagination.pages]);

  const updateFilter = (name, value) => {
    setFilters((prev) => ({
      ...prev,
      [name]: value,
      page: 1
    }));
  };

  const handlePageChange = (direction) => {
    setFilters((prev) => ({
      ...prev,
      page: Math.min(Math.max(prev.page + direction, 1), totalPages)
    }));
  };

  const handleCreate = async (payload) => {
    try {
      await hrAPI.createAttendance(payload);
      showSuccess?.('Attendance recorded');
      await fetchAttendance();
      onDataChanged?.();
      return true;
    } catch (error) {
      showError?.(error.message || 'Unable to create attendance');
      return false;
    }
  };

  const handleUpdate = async (payload) => {
    if (!modalState.record?._id) return false;
    try {
      await hrAPI.updateAttendance(modalState.record._id, payload);
      showSuccess?.('Attendance updated');
      await fetchAttendance();
      onDataChanged?.();
      return true;
    } catch (error) {
      showError?.(error.message || 'Unable to update attendance');
      return false;
    }
  };

  const handleDelete = async (record) => {
    if (!record?._id) return;
    if (!window.confirm('Delete this attendance record?')) {
      return;
    }
    try {
      setDeletingId(record._id);
      await hrAPI.deleteAttendance(record._id);
      showSuccess?.('Attendance removed');
      await fetchAttendance();
      onDataChanged?.();
    } catch (error) {
      showError?.(error.message || 'Unable to delete attendance');
    } finally {
      setDeletingId(null);
    }
  };

  const currentModalSubmit = modalState.mode === 'edit' ? handleUpdate : handleCreate;

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h3 className="text-xl font-semibold text-gray-900">Attendance</h3>
          <p className="text-sm text-gray-500">Monitor attendance, approvals, and work hours</p>
        </div>
        <button
          onClick={() => setModalState({ open: true, mode: 'create', record: null })}
          className="inline-flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Icon name="Plus" size={16} className="mr-2" />
          Add Attendance
        </button>
      </div>

      <div className="bg-white border border-gray-200 rounded-2xl p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Employee</label>
            <select
              value={filters.user}
              onChange={(event) => updateFilter('user', event.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            >
              <option value="">
                {employeesLoading ? 'Loading employees...' : 'All employees'}
              </option>
              {employeeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Status</label>
            <select
              value={filters.status}
              onChange={(event) => updateFilter('status', event.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            >
              <option value="">All statuses</option>
              {ATTENDANCE_STATUSES.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">From</label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(event) => updateFilter('startDate', event.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">To</label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(event) => updateFilter('endDate', event.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Approval</label>
            <select
              value={filters.isApproved}
              onChange={(event) => updateFilter('isApproved', event.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            >
              <option value="">All</option>
              <option value="true">Approved</option>
              <option value="false">Pending</option>
            </select>
          </div>
        </div>
        <div className="flex items-center justify-between mt-4">
          <span className="text-xs text-gray-500">Showing page {pagination.page || 1} of {totalPages}</span>
          <button
            onClick={() => setFilters({ user: '', status: '', startDate: '', endDate: '', isApproved: '', page: 1 })}
            className="text-sm text-blue-600 hover:underline"
          >
            Reset filters
          </button>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500">Employee</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500">Date</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500">Status</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500">Check In</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500">Check Out</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500">Approved</th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {loading ? (
                <tr>
                  <td colSpan="7" className="px-6 py-16 text-center text-gray-500">
                    <div className="flex items-center justify-center space-x-2">
                      <Icon name="Loader2" size={20} className="animate-spin text-blue-600" />
                      <span>Loading attendance records...</span>
                    </div>
                  </td>
                </tr>
              ) : records.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-16 text-center text-gray-500">
                    No attendance records found
                  </td>
                </tr>
              ) : (
                records.map((record) => (
                  <tr key={record._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-900">
                      <div className="font-medium">{record.user ? `${record.user.firstName || ''} ${record.user.lastName || ''}`.trim() || record.user.email : '—'}</div>
                      <div className="text-xs text-gray-500">{record.user?.role}</div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">{formatDate(record.date)}</td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${statusBadge[record.status] || 'bg-gray-100 text-gray-800'}`}>
                        {record.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">{formatTime(record.checkIn)}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{formatTime(record.checkOut)}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {record.isApproved ? (
                        <span className="inline-flex items-center text-green-600 text-xs font-semibold">
                          <Icon name="CheckCircle2" size={14} className="mr-1" /> Approved
                        </span>
                      ) : (
                        <span className="inline-flex items-center text-amber-600 text-xs font-semibold">
                          <Icon name="Clock" size={14} className="mr-1" /> Pending
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-3">
                        <button
                          onClick={() => setModalState({ open: true, mode: 'edit', record })}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          <Icon name="Edit" size={16} />
                        </button>
                        <button
                          onClick={() => handleDelete(record)}
                          disabled={deletingId === record._id}
                          className="text-red-600 hover:text-red-800 disabled:opacity-50"
                        >
                          {deletingId === record._id ? <Icon name="Loader2" size={16} className="animate-spin" /> : <Icon name="Trash2" size={16} />}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 text-sm text-gray-600">
          <span>
            Showing {(pagination.page - 1) * PAGE_SIZE + 1} -
            {' '}
            {Math.min(pagination.page * PAGE_SIZE, pagination.total || records.length)} of {pagination.total || records.length}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => handlePageChange(-1)}
              disabled={filters.page <= 1}
              className="p-2 rounded-lg border border-gray-300 text-gray-600 disabled:opacity-40"
            >
              <Icon name="ChevronLeft" size={16} />
            </button>
            <span>Page {filters.page} / {totalPages}</span>
            <button
              onClick={() => handlePageChange(1)}
              disabled={filters.page >= totalPages}
              className="p-2 rounded-lg border border-gray-300 text-gray-600 disabled:opacity-40"
            >
              <Icon name="ChevronRight" size={16} />
            </button>
          </div>
        </div>
      </div>

      <AttendanceFormModal
        open={modalState.open}
        mode={modalState.mode}
        initialData={modalState.record}
        employeeOptions={employeeOptions}
        employeesLoading={employeesLoading}
        onClose={() => setModalState({ open: false, mode: 'create', record: null })}
        onSubmit={currentModalSubmit}
      />
    </div>
  );
};

export default AttendanceTab;
